import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import storage from "../../../utils/storage";
import { useDispatch, useSelector } from "react-redux";
import appActions from "../../../appActions";
import PrimeTable from "../../../components/PrimeTable";
import SiteGroupForm from "./SiteGroupForm";
import SiteGroupFilterBar from "./SiteGroupFilterBar";
import SiteGroupHeader from "./SiteGroupHeader";
import Sidebar from "../../../components/Sidebar";

const SiteGroupList = () => {

    const dispatch = useDispatch();

    const userInfo = useMemo(() => JSON.parse(storage.get('loginUser')), []);
  //  const { userRole, userId, siteId } = userInfo;

    const { siteGroupList, siteGroupDetails } = useSelector((state) => state?.sitegroup);

    const defaultParams = useMemo(() => ({
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder : -1,
    }), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [filterParams, setFilterParams] = useState({});

    // New states for dialog control and selected user
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [selectedSiteGroup, setSelectedSiteGroup] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isCopyMode, setIsCopyMode] = useState(false);

    const fetchSiteGroup = useCallback((params = {}) => {
        dispatch(appActions.SITEGROUP_GET_ALL_REQUEST({ ...defaultParams, ...params }));
        dispatch(appActions.SITEGROUPLIST_GET_ALL_REQUEST({ ...params }));
    }, [dispatch, defaultParams]);

    const handleFilterTask = useCallback((params) => {
        setFilterParams(params);
        fetchSiteGroup(params);
    }, [fetchSiteGroup]);

    useEffect(() => {
        fetchSiteGroup();
    }, [dispatch]);

    const handleCopyRedirect = (record) => {
        const data = siteGroupList?.data?.find((t) => t.id === record.id);
        setSelectedSiteGroup(data);
        setIsViewMode(false);
        setIsEditMode(false);
        setIsCopyMode(true);
        setIsDialogVisible(true); // Open dialog in view mode
    };

    const handleViewRedirect = (record) => {
        const data = siteGroupList?.data?.find((t) => t.id === record.id);
        setSelectedSiteGroup(data);
        setIsViewMode(true);
        setIsEditMode(false);
        setIsDialogVisible(true); // Open dialog in view mode
    };

    const handleEditRedirect = (record) => {
        const data = siteGroupList?.data?.find((t) => t.id === record.id);
        setSelectedSiteGroup(data);
        setIsEditMode(true);
        setIsViewMode(false);
        setIsDialogVisible(true); // Open dialog in edit mode
    };

    const handleDelete = useCallback((record) => {
        const { id } = record;
        dispatch(appActions.SITEGROUP_DELETE_REQUEST({ id }));
    }, [dispatch]);

    const handleDialogClose = () => {
        setIsDialogVisible(false);
        setSelectedSiteGroup(null);
    };

    const handleDialogSave = () => {
        fetchSiteGroup();
        setIsDialogVisible(false);
    }

    const handleSearch = useCallback(
        (params) => {

            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };

            dispatch(appActions.SITEGROUP_GET_ALL_REQUEST(searchParams));
        },
        [dispatch],
    );

    const onFilterChangeCallback  = useCallback(
        (event) => {

            const { sortField, sortOrder, rows, page } = event;
            
            const params = {...formValues, pagesize: rows, pageno: page, sort: sortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); 
        },

        [handleSearch, formValues],
    );

    
    useEffect(() => {
        if (siteGroupDetails?.action === 'delete' || siteGroupDetails?.action === 'create' || siteGroupDetails?.action === 'update') {
            fetchSiteGroup();
        }
    }, [siteGroupDetails, fetchSiteGroup]);

    const columns = [
        { field: 'siteCode', header: 'Site Group Code', sortable: true},
        { field: 'siteName', header: 'Site Group Name', sortable: true},
        { field: 'status', header: 'Status', sortable: true},
    ];

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                    <i className="pi pi-eye text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                    <i className="pi pi-pencil text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleCopyRedirect(rowData)} data-pr-tooltip="Copy">
                    <i className="pi pi-copy text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                    <i className="pi pi-times-circle text-base text-danger"/>
                </span>
            </div>
        ),
        [handleCopyRedirect, handleViewRedirect, handleEditRedirect, handleDelete],
    );

    return (
        <div className="w-full">
           
            <SiteGroupHeader />
            <div className="flex flex-min-height">
                <Sidebar />
                <div className="p-grid w-full pl-60px">
                    <h3 className="title">Site Group Management</h3>
                    <SiteGroupFilterBar
                        handleFilter={handleFilterTask}
                        filterParams={filterParams}
                        userInfo={userInfo}
                        handleSearch={handleSearch}
                    />
                    <PrimeTable
                        list={siteGroupList}
                        columns={columns}
                        actionColumn={actionTemplate}
                        tableName='site_group_management'
                        isLazy={true}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        isExport={false}
                    />
                </div>

                {/* Dialog for User Form */}
                <Dialog
                    visible={isDialogVisible}
                    header={isEditMode ? "Edit Site" : isCopyMode ? "Copy Site" : "View Site"}
                    onHide={handleDialogClose}
                    style={{ width: '50vw' }}
                >
                    <SiteGroupForm
                        siteGroup={selectedSiteGroup}
                        isEditMode={isEditMode}
                        isViewMode={isViewMode}
                        isCopyMode={isCopyMode}
                        onClose={handleDialogClose}
                        onSave={handleDialogSave}
                    />
                </Dialog>
            </div>
        </div>
    );
}

export default SiteGroupList;
